import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Tooltip, OverlayTrigger, Button } from "react-bootstrap";
import DataTable from "react-data-table-component";
import DateTime from "../../../../components/Formatters/DateTime";
import TextSpinner from "../../../../components/UI/Spinner/TextSpinner";
import useDetalhamento from "../hooks/useDetalhamento";
import FormDemonstrativos from "../FormDemonstrativos";
import useMontarLogica from "../hooks/useMontarLogica";
import useExcluirDemonstrativo from "../hooks/useExcluirDemonstrativo";
import constants from "../../../Constants";

const getTituloValorDeclarado = (tipoDetalhamento) => {
  switch (tipoDetalhamento) {
    case "Q":
      return <div>Crédito Indevido (R$)</div>;
    case "Q1":
      return <div>Crédito Indevido (R$)</div>;
    case "Q2":
      return <div>Saldo credor do período anterior na apuração (R$)</div>;
    case "Q3":
      return <div>Crédito apropriado (R$)</div>;
    default:
      return <div>Crédito Indevido (R$)</div>;
  }
};

const getTituloValorApurado = (tipoDetalhamento) => {
  switch (tipoDetalhamento) {
    case "Q":
      return <div>Saldo Credor/Deduções (R$)</div>;
    case "Q2":
      return <div>Saldo credor do período anterior transportado (R$)</div>;
    default:
      return <div>Saldo Credor/Deduções (R$)</div>;
  }
};

const getColumns = (tipoDetalhamento) =>
  [
    {
      name: <div>Referência</div>,
      cell: (row) => row.txReferencia,
      minWidth: "10%",
      center: true,
    },
    {
      name: <div>Data Vencimento</div>,
      minWidth: "10%",
      cell: (row) => <DateTime value={row.dtVencimento} stringFormat="dd/MM/yyyy" />,
      center: true,
    },
    {
      name: getTituloValorDeclarado(tipoDetalhamento),
      cell: (row) =>
        row.vlDeclarado
          ? row.vlDeclarado.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: tipoDetalhamento === "Q2" ? "22%" : "10%",
      center: true,
    },
    (tipoDetalhamento === "Q" || tipoDetalhamento === "Q2") && {
      name: getTituloValorApurado(tipoDetalhamento),
      cell: (row) =>
        row.vlApurado
          ? row.vlApurado.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "22%",
      center: true,
    },
    {
      name: <div>ICMS a Recolher (R$)</div>,
      cell: (row) =>
        row.vlIcms
          ? row.vlIcms.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "10%",
      center: true,
    },
    {
      name: <div>Multa (R$)</div>,
      cell: (row) =>
        row.vlMultaOcorrenciaAtual
          ? row.vlMultaOcorrenciaAtual.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "10%",
      center: true,
    },
    {
      name: <div>ICMS + Multa (R$) </div>,
      cell: (row) =>
        (row.vlMultaOcorrenciaAtual + row.vlIcms).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),

      minWidth: "10%",
      center: true,
    },
  ].filter(Boolean);

const conditionalRowStyles = [
  {
    when: (row) => row.txReferencia === "Total",
    style: {
      backgroundColor: "#f0f0f0",
      cursor: "not-allowed",
      fontWeight: "bold", // Deixa a fonte em negrito
      color: "#333", // (opcional) ajusta a cor da fonte pra ficar mais visível
    },
  },
];

const ListagemQ = ({ history, sqItemOcorrencia, sqAcaoFiscal, tipoDetalhamento }) => {
  const [reload, setReload] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const { dataWithTotal, totalRows, isLoading, handlePageChange } = useDetalhamento({
    sqAcaoFiscal,
    sqItemOcorrencia,
    reload, // Passando para o hook para atualizar os dados
  });
  const [dados, setDados] = useState({});

  const { montarLogica, mostrarLista, setMostrarLista } = useMontarLogica();

  useEffect(() => {
    if (mostrarLista) {
      montarLogica(1);
    }
  }, [montarLogica, mostrarLista, reload]);

  const voltarListagem = () => {
    setMostrarLista(true);
    setReload((prev) => !prev);
  };

  const { ModalExcluirDemonstrativo, setShowExcluirConfirm } = useExcluirDemonstrativo({
    setReload,
    setItensSelecionados,
  });

  const columns = useMemo(() => getColumns(tipoDetalhamento), [tipoDetalhamento]);

  const preparaDelete = useCallback(() => {
    if (itensSelecionados.length > 0) {
      setShowExcluirConfirm({
        show: true,
        body: `Confirma exclusão do(s) item(ns) no demonstrativo?`,
        context: { sqAcaoFiscal, sqItemOcorrencia, listaItens: itensSelecionados },
      });

      // Resetando a seleção e forçando novo fetch dos dados
      setClearSelectedRows(!clearSelectedRows);
      setItensSelecionados([]);

      // Força o reload completo para pegar os dados atualizados
      setReload((prev) => !prev);
    } else {
      document.getElementById("alerta").innerHTML = "Selecione pelo menos 1 item para excluir";
    }
  }, [itensSelecionados, sqAcaoFiscal, sqItemOcorrencia, setShowExcluirConfirm, clearSelectedRows]);

  return mostrarLista ? (
    <>
      {ModalExcluirDemonstrativo}
      <DataTable
        noHeader
        keyField={(row, index) => `${row.sqItemOcorrencia}-${index}`}
        columns={columns}
        data={dataWithTotal}
        progressPending={isLoading}
        noDataComponent="Nenhum registro para exibir"
        progressComponent={<TextSpinner />}
        pointerOnHover
        onRowClicked={(row) => {
          if (row.txReferencia !== "Total") {
            setDados(row);
            montarLogica(4);
          }
        }}
        conditionalRowStyles={conditionalRowStyles}
        pagination
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        paginationComponentOptions={constants.paginationComponentOptions}
        paginationPerPage={10}
        selectableRows
        selectableRowDisabled={(row) => row.txReferencia === "Total"}
        selectableRowsVisibleOnly={(row) => !(row.txReferencia === "Total")}
        onSelectedRowsChange={(state) => {
          setItensSelecionados(state.selectedRows);
        }}
        clearSelectedRows={clearSelectedRows}
      />
      <span className="alerta" hidden={itensSelecionados.length > 0} id="alerta">
        &nbsp;
      </span>

      <br />
      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-chave">Excluir itens do demonstrativo</Tooltip>}>
        <Button
          className="btn btn-primary btn-md mt-1" // btn btn-primary btn-md mr-2 btn btn-primary
          style={{ marginTop: "3px" }} // CSS inline direto
          disabled={itensSelecionados.length === 0}
          onClick={() => preparaDelete()}
        >
          Excluir
        </Button>
      </OverlayTrigger>
    </>
  ) : (
    <FormDemonstrativos
      tipoDetalhamento={dados.tipoDetalhamento}
      sqItemOcorrencia={dados.sqItemOcorrencia}
      sqAcaoFiscal={dados.sqAcaoFiscal}
      dadosOcorrencia={dados}
      history={history}
      voltarListagem={() => voltarListagem()}
    />
  );
};

export default ListagemQ;
